import React, { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import Fab from '@mui/material/Fab';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import ImageIcon from '@mui/icons-material/Image';
import HideImageIcon from '@mui/icons-material/HideImage';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import BlurOffIcon from '@mui/icons-material/BlurOff';
import useBackgroundEffect from '../../../hooks/useBackgroundEffect/useBackgroundEffect';
import { BackgroundBlurIntensityControl } from './BackgroundBlurIntensityControl/BackgroundBlurIntensityControl';
import { CompositeBackgroundButtonSXStyles } from './style';

export default function CompositeBackgroundButton(props: { disabled?: boolean }) {
  const {
    currentEffect,
    blurIntensity,
    isSupported,
    isApplying,
    selectedImageName,
    error,
    applyBlur,
    applyCustomBackground,
    clearEffect,
    setBlurIntensity,
  } = useBackgroundEffect();
  const {
    compositeBackgroundButtonFab,
    compositeBackgroundButtonMenuPaper,
    compositeBackgroundButtonHeader,
    compositeBackgroundButtonTitle,
    compositeBackgroundButtonHelper,
    compositeBackgroundButtonHelperText,
    compositeBackgroundButtonErrorText,
    compositeBackgroundButtonDivider,
    compositeBackgroundButtonMenuItem,
  } = CompositeBackgroundButtonSXStyles;
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isBlurPanelPending, setIsBlurPanelPending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const translateBackgroundLabel = useCallback((key: string) => (i18n.exists(key) ? t(key) : key), [i18n, t]);

  const disabled = props.disabled;
  const isBlurPanelOpen = currentEffect === 'blur' || isBlurPanelPending;

  const icon = useMemo(() => {
    if (currentEffect === 'blur') {
      return <BlurOnIcon />;
    }
    if (currentEffect === 'image') {
      return <ImageIcon />;
    }
    return <HideImageIcon />;
  }, [currentEffect]);

  const tooltipText = useMemo(() => {
    if (!isSupported) {
      return translateBackgroundLabel('Background Effects Not Supported');
    }
    if (currentEffect === 'blur') {
      return translateBackgroundLabel('Blur Background');
    }
    if (currentEffect === 'image') {
      return translateBackgroundLabel('Custom Background');
    }
    return translateBackgroundLabel('Background Effects');
  }, [currentEffect, isSupported, translateBackgroundLabel]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setIsBlurPanelPending(false);
  };

  const handleApplyBlur = async () => {
    setIsBlurPanelPending(true);
    await applyBlur();
  };

  const handleApplyNone = () => {
    setIsBlurPanelPending(false);
    clearEffect();
    handleMenuClose();
  };

  const handleCustomBackgroundClick = () => {
    setIsBlurPanelPending(false);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await applyCustomBackground(file);
    }
    event.target.value = '';
    handleMenuClose();
  };

  return (
    <div>
      <Tooltip title={tooltipText} placement="top" PopperProps={{ disablePortal: true }}>
        <span>
          <Fab
            sx={compositeBackgroundButtonFab}
            size={'large'}
            onClick={handleMenuOpen}
            disabled={disabled || isApplying}
            data-cy-background-toggle
          >
            {isApplying ? <CircularProgress size={24} color="inherit" /> : icon}
          </Fab>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        keepMounted
        PaperProps={{ sx: compositeBackgroundButtonMenuPaper }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={compositeBackgroundButtonHeader}>
          <Typography sx={compositeBackgroundButtonTitle}>{translateBackgroundLabel('background-effects')}</Typography>
        </Box>
        {!isSupported ? (
          <Box sx={compositeBackgroundButtonHelper}>
            <Typography sx={compositeBackgroundButtonHelperText}>
              {translateBackgroundLabel('Background Effects Not Supported')}
            </Typography>
          </Box>
        ) : null}
        {error ? (
          <Box sx={compositeBackgroundButtonHelper}>
            <Typography sx={compositeBackgroundButtonErrorText}>{error}</Typography>
          </Box>
        ) : null}
        <Divider sx={compositeBackgroundButtonDivider} />
        <MenuItem
          onClick={handleApplyBlur}
          selected={isBlurPanelOpen}
          disabled={disabled || isApplying || !isSupported}
          sx={compositeBackgroundButtonMenuItem}
        >
          <ListItemIcon>
            <BlurOnIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={translateBackgroundLabel('blur-effect')} />
        </MenuItem>
        {isBlurPanelOpen ? (
          <BackgroundBlurIntensityControl
            ariaLabel={translateBackgroundLabel('Background blur intensity')}
            blurIntensity={blurIntensity}
            disabled={disabled || isApplying}
            label={translateBackgroundLabel('blur-intensity')}
            onChange={setBlurIntensity}
          />
        ) : null}
        <MenuItem
          onClick={handleCustomBackgroundClick}
          selected={currentEffect === 'image'}
          disabled={disabled || isApplying || !isSupported}
          sx={compositeBackgroundButtonMenuItem}
        >
          <ListItemIcon>
            <WallpaperIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              selectedImageName
                ? translateBackgroundLabel('change-background')
                : translateBackgroundLabel('custom-background')
            }
            secondary={selectedImageName}
            primaryTypographyProps={{ noWrap: true }}
            secondaryTypographyProps={{ noWrap: true, title: selectedImageName }}
          />
        </MenuItem>
        <MenuItem
          onClick={handleApplyNone}
          selected={currentEffect === 'none'}
          disabled={disabled || isApplying || currentEffect === 'none'}
          sx={compositeBackgroundButtonMenuItem}
        >
          <ListItemIcon>
            <BlurOffIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={translateBackgroundLabel('no-background')} />
        </MenuItem>
      </Menu>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
    </div>
  );
}
